/**
 * Bannière d'installation PWA + notifications.
 *
 * « Installer le raccourci » fait les deux gestes d'un coup : demande la
 * permission de notification (avec abonnement push envoyé au serveur) puis
 * déclenche l'invite d'installation du navigateur. La croix masque la bannière
 * durablement (localStorage) — on ne harcèle pas.
 *
 * Chrome/Edge/Android exposent `beforeinstallprompt` ; Safari iOS non — la
 * bannière n'y propose que les notifications (l'installation passe par
 * « Partager → Sur l'écran d'accueil », hors de notre contrôle).
 */
import { useEffect, useState } from 'react';
import { envoyer } from '../reseau.ts';
import { useLibelles } from '../i18n.tsx';

const CLE_MASQUEE = 'eventmatch.banniere-installation.masquee';

type EvenementInstallation = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let invitationCapturee: EvenementInstallation | null = null;

// Capturée au niveau module : l'événement part souvent avant le montage React.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    invitationCapturee = e as EvenementInstallation;
  });
}

async function abonnerAuxNotifications(langue: string): Promise<boolean> {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  try {
    const enregistrement = await navigator.serviceWorker.ready;
    const { cle } = await (await fetch('/api/notifications/cle-publique')).json();
    if (!cle) return false;

    const abonnement = await enregistrement.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: cle,
    });
    const json = abonnement.toJSON();
    await envoyer('/api/notifications/abonnement', {
      endpoint: abonnement.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      langue,
    });
    return true;
  } catch {
    return false;
  }
}

export function BanniereInstallation() {
  const { t, langue } = useLibelles();
  const [visible, setVisible] = useState(false);
  const [etat, setEtat] = useState<'repos' | 'en_cours' | 'fait'>('repos');

  useEffect(() => {
    try {
      if (localStorage.getItem(CLE_MASQUEE) === '1') return;
    } catch {
      /* stockage indisponible */
    }
    // Déjà installée (mode standalone) : la bannière n'a plus de raison d'être.
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    setVisible(true);
  }, []);

  if (!visible) return null;

  const masquer = () => {
    setVisible(false);
    try {
      localStorage.setItem(CLE_MASQUEE, '1');
    } catch {
      /* stockage indisponible */
    }
  };

  const installer = async () => {
    setEtat('en_cours');
    await abonnerAuxNotifications(langue);
    if (invitationCapturee) {
      await invitationCapturee.prompt();
      await invitationCapturee.userChoice;
      invitationCapturee = null;
    }
    setEtat('fait');
    setTimeout(masquer, 1500);
  };

  return (
    <div className="relative z-50 bg-charbon" role="complementary">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-3 text-center sm:justify-between sm:text-left">
        <p className="text-[13px] text-blanc">
          <span className="font-medium">{t('pwa.banniere.titre', 'Connectez en un clic aux événements !')}</span>{' '}
          <span className="text-fumee">
            {t(
              'pwa.banniere.description',
              "Recevez les notifications d'événements et de Perfect Match d'EventMatch.",
            )}
          </span>
        </p>
        <div className="flex items-center gap-3">
          <button
            className="btn-creme !min-h-0 px-4 py-1.5 text-[13px]"
            onClick={() => void installer()}
            disabled={etat !== 'repos'}
          >
            {etat === 'fait'
              ? t('pwa.banniere.fait', 'Activé ✓')
              : etat === 'en_cours'
                ? '…'
                : t('pwa.banniere.installer', 'Installer le raccourci')}
          </button>
          <button
            onClick={masquer}
            aria-label={t('pwa.banniere.fermer', 'Masquer')}
            className="flex h-8 w-8 items-center justify-center text-fumee transition-colors hover:text-blanc"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
